import { createRouter, createWebHistory } from 'vue-router';
import { storeToRefs } from 'pinia';
import BuyCryptoView from '@/views/BuyCryptoView.vue';
import HomeView from '@/views/HomeView.vue';
import KycView from '@/views/KycView.vue';
import AuthView from '@/views/AuthView.vue';
import PaymentView from '@/views/PaymentView.vue';
import TransactionDetails from '@/views/TransactionDetailsView.vue';
import AllTransactions from '@/views/AllTransactionsView.vue';
import ProfileView from '@/views/ProfileView.vue';
import LandingView from '@/views/LandingView.vue';
import PayoutAccounts from '@/views/PayoutAccounts.vue';
import FiatSettlementView from '@/views/FiatSettlementView.vue';
import SellCrypto from '@/components/sell/SellCrypto.vue';
import OrderSummary from '@/components/transact/OrderSummary.vue';
import TransactionLimit from '@/components/general/TransactionLimit.vue';
import { useAuthStore } from '@/store/modules/auth';
import { useInterceptorStore } from '@/store/modules/interceptor';
import { useKycStore } from '@/store/modules/kyc';
import { usePaymentStore } from '@/store/modules/payment';
import useTransactionsStore from '@/store/modules/transactions';
import { usePayoutAccountsStore } from '@/store/modules/payout-accounts';
import { KycTypes } from '@/types/kyc';
import { PaymentTypes } from '@/types/payment';
import { TransactTypes } from '@/types/transact';
import { PayoutAccountsTypes } from '@/types/payout-accounts';
import { RouteNames, RoutePaths, unprotectedRoutes } from './route-details';
export { RouteNames, RoutePaths, unprotectedRoutes };
const routes = [
    {
        path: `${RoutePaths.Landing}/:apiKey?`,
        name: RouteNames.Landing,
        component: LandingView,
    },
    {
        path: RoutePaths.Home,
        name: RouteNames.Home,
        component: HomeView,
    },
    {
        path: RoutePaths.Transact,
        name: RouteNames.Transact,
        component: BuyCryptoView,
    },
    {
        path: RoutePaths.Kyc,
        name: RouteNames.Kyc,
        component: KycView,
    },
    {
        path: RoutePaths.Auth,
        name: RouteNames.Auth,
        component: AuthView,
    },
    {
        path: RoutePaths.Payment,
        name: RouteNames.Payment,
        component: PaymentView,
    },
    {
        path: RoutePaths.Transaction,
        name: RouteNames.Transaction,
        component: TransactionDetails,
    },
    {
        path: RoutePaths.AllTransactions,
        name: RouteNames.AllTransactions,
        component: AllTransactions,
    },
    {
        path: RoutePaths.Profile,
        name: RouteNames.Profile,
        component: ProfileView,
    },
    {
        path: RoutePaths.Sell,
        name: RouteNames.Sell,
        component: SellCrypto,
    },
    {
        path: RoutePaths.SellSummary,
        name: RouteNames.SellSummary,
        component: OrderSummary,
    },
    {
        path: RoutePaths.TransactionLimit,
        name: RouteNames.TransactionLimit,
        component: TransactionLimit,
    },
    {
        path: RoutePaths.PayoutAccounts,
        name: RouteNames.PayoutAccounts,
        component: PayoutAccounts,
    },
    {
        path: RoutePaths.Authorize,
        name: RouteNames.Authorize,
        component: FiatSettlementView,
    },
];
const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes,
});
router.beforeEach((to, from, next) => {
    const isProtected = !unprotectedRoutes.includes(to.name);
    const auth = useAuthStore();
    const kyc = useKycStore();
    const transaction = useTransactionsStore();
    const { partnerVerified, standaloneAuthFlow } = storeToRefs(auth);
    const { kycStatus, currentStep: KycStep } = storeToRefs(kyc);
    const paymentStore = usePaymentStore();
    const { currentStep: paymentCurrentStep } = storeToRefs(paymentStore);
    const { currentStep: buyCurrentStep, txType } = storeToRefs(transaction);
    const payoutAccountsStore = usePayoutAccountsStore();
    const { currentStep: payoutAccountsStep } = storeToRefs(payoutAccountsStore);
    if (!partnerVerified.value) {
        if (to.name !== RouteNames.Landing) {
            const path = process.env.VUE_APP_ENVIRONMENT === 'staging'
                ? `${RoutePaths.Landing}/533fc4c3eaeb2a8f297a740ea178d830`
                : RoutePaths.Landing;
            next({ path });
        }
        else {
            next();
        }
    }
    else if (to.name === RouteNames.Authorize) {
        // Partner scoped and login free. The checks below read step/kyc/txType
        // state that sessionStorage can still hold from an earlier buy or sell in
        // the same tab, so none of them may divert this flow.
        next();
    }
    else {
        // case where widget is being used to ONLY kyc a customer
        /* eslint-disable-next-line */
        if (txType.value === TransactTypes.TransactionType.KYC) {
            if (!auth.isLoggedIn) {
                standaloneAuthFlow.value = true;
                if (to.name !== 'auth') {
                    next({ path: '/auth' });
                }
                next();
            }
            else {
                if (to.name !== 'kyc') {
                    next({ path: '/kyc' });
                }
                next();
            }
        }
        else {
            /* eslint-disable-next-line */
            if (!auth.isLoggedIn) {
                if (isProtected) {
                    buyCurrentStep.value = TransactTypes.TransactSteps.BUY_1;
                    next({ path: RoutePaths.Transact });
                }
                else {
                    if (to.name === RouteNames.Auth) {
                        standaloneAuthFlow.value = true;
                    }
                    next();
                    /* eslint-disable-next-line */
                    return;
                }
            }
            else {
                // exception for payments so that wallet address can be put in for order creation
                const allowedViews = [
                    PaymentTypes.PaymentSteps.PAYMENT_1,
                    PaymentTypes.SellPaymentSteps.PAYMENT_2,
                    PaymentTypes.SellPaymentSteps.SAVE_PAYOUT_ACCOUNT,
                    PayoutAccountsTypes.PayoutComponents.FormSelect,
                    PayoutAccountsTypes.PayoutComponents.ManagePayouts,
                    TransactTypes.TransactSteps.BUY_3,
                    TransactTypes.TransactSteps.BUY_2,
                    TransactTypes.TransactSteps.BUY_1,
                ];
                const allowedKycState = [
                    KycTypes.KycStatus.APPROVED,
                    KycTypes.KycStatus.INCOMPLETE,
                ].includes(kycStatus.value);
                const allowPaymentRoute = to.name === RouteNames.Payment &&
                    allowedViews.includes(paymentCurrentStep.value) &&
                    allowedKycState;
                const allowTransactRoute = to.name === RouteNames.Transact &&
                    allowedViews.includes(buyCurrentStep.value) &&
                    allowedKycState;
                const allowPayoutAccountRoute = to.name === RouteNames.PayoutAccounts &&
                    allowedViews.includes(payoutAccountsStep.value) &&
                    allowedKycState;
                if (allowTransactRoute ||
                    allowPaymentRoute ||
                    allowPayoutAccountRoute) {
                    next();
                    /* eslint-disable-next-line */
                    return;
                    /* eslint-disable-next-line */
                }
                else if (to.name !== RouteNames.Kyc) {
                    // if going to other routes without completing kyc always route to kyc,
                    if (kycStatus.value === KycTypes.KycStatus.INCOMPLETE) {
                        KycStep.value = KycTypes.KycSteps.KYC_1;
                        next({ path: RoutePaths.Kyc });
                    }
                    else if (to.name === RouteNames.Transact &&
                        kycStatus.value !== KycTypes.KycStatus.APPROVED) {
                        next({ path: RoutePaths.Home });
                    }
                    else {
                        next();
                        /* eslint-disable-next-line */
                        return;
                    }
                }
                next();
                /* eslint-disable-next-line */
                return;
            }
        }
    }
});
router.afterEach((to, from) => {
    if (to.name === RouteNames.PayoutAccounts) {
        const payoutAccounts = usePayoutAccountsStore();
        if (from.name === RouteNames.Home) {
            payoutAccounts.resetStep();
        }
    }
    /* Set query params from landing */
    if (from.name === RouteNames.Landing &&
        to.name === RouteNames.Transact &&
        Object.keys(from.query).length) {
        const interceptorStore = useInterceptorStore();
        interceptorStore.setQueryParams(from.query);
    }
});
export default router;
